/**
 * BL-87 — the global error boundary / app-shell net.
 *
 * Before this, the only net was the per-frame try/catch in GameLoop
 * (engine/GameLoop.ts) — fine for mid-wave throws, but blind to an async
 * rejection (`void submitScore()` / `void save()`), an EventBus-callback
 * throw, or a post-boot throw outside the loop. Those surfaced only to
 * devtools, so a field crash was invisible. This installs `window`-level
 * `error` + `unhandledrejection` handlers that:
 *   (a) `console.error` a stable-prefixed, **PII-free** payload,
 *   (b) show a recoverable in-game notice (reusing the boot banner pattern),
 *   (c) swallow the event so the app stays alive where safe.
 *
 * Privacy (Dim N): the logged payload is message + stack + build id ONLY —
 * never `playerId`, initials, or save contents. The optional opt-in crash
 * beacon is deliberately NOT wired here (owner privacy + infra decision).
 *
 * Lives in `bootstrap` (DOM-touching), never `core` — `core` stays DOM-pure.
 */

const LOG_PREFIX = "[ErrorBoundary]";

export interface ErrorBoundaryDeps {
  /** Show a recoverable in-game notice (e.g. the boot/SAVE-DATA banner). */
  notify: (message: string) => void;
  /** PII-free build identifier (e.g. __APP_VERSION__). */
  buildId: string;
}

/** A PII-free crash record — message + stack + build id, nothing else. */
interface CrashReport {
  kind: "error" | "unhandledrejection";
  message: string;
  stack: string | null;
  buildId: string;
}

function toMessage(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  try {
    return String(value);
  } catch {
    return "unknown error";
  }
}

function toStack(value: unknown): string | null {
  return value instanceof Error && typeof value.stack === "string"
    ? value.stack
    : null;
}

/**
 * Install the global error boundary. Returns a disposer that removes the
 * listeners (used by tests; the app keeps them for its whole lifetime).
 */
export function installErrorBoundary(deps: ErrorBoundaryDeps): () => void {
  const report = (kind: CrashReport["kind"], reason: unknown): void => {
    const crash: CrashReport = {
      kind,
      message: toMessage(reason),
      stack: toStack(reason),
      buildId: deps.buildId,
    };
    // PII-free by construction — never log playerId / initials / save data.
    console.error(`${LOG_PREFIX} uncaught ${kind}:`, crash);

    // TODO(owner): opt-in, PII-free crash beacon to the same-origin API.
    // Deliberately NOT wired — needs an endpoint + an owner privacy decision
    // (interacts with BL-95/BL-96). The console + recoverable notice is the
    // self-contained, no-PII win for this session.

    try {
      deps.notify("SYSTEM RECOVERED — A GLITCH WAS CONTAINED");
    } catch (e) {
      // The notice path itself must never re-throw out of the handler.
      console.error(`${LOG_PREFIX} notice failed:`, e);
    }
  };

  const onError = (event: ErrorEvent): void => {
    report("error", event.error ?? event.message);
    // Swallow: we've logged + shown a notice; keep the app alive.
    event.preventDefault();
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    report("unhandledrejection", event.reason);
    event.preventDefault();
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
